// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Party & bulk planner  (route /party — "Party & Feast Planner", linked from the profile page)
// For a gathering: a rough portion guide (event type × guests → a suggested total, data/party — a guide, not a promise), then the
// customer builds their own crate from the REAL menu (utils/picks + components/catch/CatchPicker: catch, cut, 500 g / 1 kg packs) with a
// live weight-vs-suggestion bar and total, and adds it all to the normal cart — they pay at checkout like any order. Open to everyone.
// Deliberately NOT here (no data/system behind it): pre-made "chef curated platters" with invented contents/prices, bulk tier discounts
// (−10 % / −18 % / "save 15 %"), free marination and rubs, "clean-room" claims, cold-chain / "< 4 °C for 12 hours" / bio-crate
// claims, advance booking up to 14 days with a "harbor auction price lock", a 20 % advance payment, a Seattle address, a US phone
// number. Big or rare orders point to the real WhatsApp/phone channel instead.
// Neighbours: hooks/useCart, utils/picks, data/party, data/delivery.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Minus, Phone, Plus, ShoppingCart, Users } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa6";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import CatchPicker from "../components/catch/CatchPicker";
import useCommercialRules from "../hooks/useCommercialRules";
import { CONTACT_PHONE_DISPLAY, CONTACT_PHONE_HREF, SUPPORT_HOURS } from "../data/legalContact";
import { DEFAULT_GUESTS, EVENT_PROFILES, MAX_GUESTS, MIN_GUESTS, recommendedKg } from "../data/party";
import { seafoodItems } from "../data/seafood";
import { getWhatsAppLink } from "../data/whatsappContext";
import useCart from "../hooks/useCart";
import { MAX_PACKS } from "../hooks/useCatchDraft";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { MAX_PER_SIZE, pickOf, totals, type Picks } from "../utils/picks";
import { formatInr } from "../utils/priceFormat";

export default function PartyPlanner() {
  useDocumentTitle("Party & bulk planner");
  const rules = useCommercialRules();
  const { qtyOf, setQty } = useCart();
  const [profileId, setProfileId] = useState(EVENT_PROFILES[0].id);
  const [guests, setGuests] = useState(DEFAULT_GUESTS);
  const [picks, setPicks] = useState<Picks>({});
  const [added, setAdded] = useState(false);

  const profile = EVENT_PROFILES.find((p) => p.id === profileId) ?? EVENT_PROFILES[0];
  const suggested = recommendedKg(guests, profile);
  const { kg, price, packs } = totals(picks);
  const pct = Math.min(100, suggested > 0 ? Math.round((kg / suggested) * 100) : 0);

  const changePicks = (next: Picks) => {
    setAdded(false);
    setPicks(next);
  };

  const addAll = () => {
    for (const item of seafoodItems) {
      const pick = pickOf(picks, item);
      for (const size of ["500g", "1kg"] as const) {
        if (pick[size] > 0) setQty(item.id, size, Math.min(MAX_PACKS, qtyOf(item.id, size) + pick[size]), pick.cut);
      }
    }
    setAdded(true);
  };

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-44 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/menu" aria-label="Back to the menu" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Party &amp; feast planner</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Plan a big order for a gathering</p>
            </div>
          </header>

          <Link to="/coming-soon/feast-platters" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Chef-curated feast platters</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <section className="rounded-2xl bg-ocean-700/5 p-4 dark:bg-white/5">
            <p className="text-xs font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300">Hosting a gathering?</p>
            <p className="mt-1 text-sm text-ocean-800/90 dark:text-foam-100/85">Tell us the occasion and the guest count for a rough idea of how much to plan, then pick your catches, cuts and pack sizes. It goes into your normal cart at the menu prices — delivery is ₹{rules.deliveryFee} per order{rules.freeDeliveryAbove !== null ? `, free above ₹${rules.freeDeliveryAbove}` : ""}.</p>
          </section>

          <section aria-labelledby="est-h" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <div className="flex items-center justify-between">
              <h2 id="est-h" className="flex items-center gap-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50"><Users className="h-5 w-5" aria-hidden="true" /> Portion guide</h2>
              <span className="rounded-full bg-teal-100 px-2.5 py-0.5 text-[11px] font-bold text-teal-700">A rough guide</span>
            </div>
            <div role="radiogroup" aria-label="Occasion" className="-mx-1 mt-3 flex gap-2 overflow-x-auto px-1 pb-1">
              {EVENT_PROFILES.map((option) => {
                const on = option.id === profileId;
                return (
                  <button key={option.id} type="button" role="radio" aria-checked={on} onClick={() => setProfileId(option.id)} className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${on ? "bg-ocean-800 text-white shadow-sm" : "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50"}`}>
                    {option.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5">
                <p id="guests-l" className="text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:text-foam-100">Guests</p>
                <div className="mt-1 flex items-center justify-between">
                  <button type="button" aria-label="Fewer guests" disabled={guests <= MIN_GUESTS} onClick={() => setGuests(guests - 1)} className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-ocean-900 disabled:opacity-40 dark:bg-ocean-950 dark:text-foam-50"><Minus className="h-4 w-4" aria-hidden="true" /></button>
                  <output aria-labelledby="guests-l" className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">{guests}</output>
                  <button type="button" aria-label="More guests" disabled={guests >= MAX_GUESTS} onClick={() => setGuests(guests + 1)} className="flex h-10 w-10 items-center justify-center rounded-full bg-ocean-800 text-white disabled:opacity-40"><Plus className="h-4 w-4" aria-hidden="true" /></button>
                </div>
              </div>
              <div className="rounded-xl bg-teal-100/50 p-3 dark:bg-teal-700/10">
                <p className="text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:text-foam-100">Suggested total</p>
                <p className="mt-1 font-display text-2xl font-bold text-teal-700 dark:text-teal-300" data-testid="suggested">~{suggested} kg</p>
                <p className="text-[11px] text-ocean-800/80 dark:text-foam-100/75">about {Math.round(profile.kgPerGuest * 1000)} g per guest, before cleaning</p>
              </div>
            </div>
          </section>

          <section aria-labelledby="crate-h" className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 id="crate-h" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Build your crate</h2>
              <span className="text-xs text-ocean-800/80 dark:text-foam-100/75">Up to {MAX_PER_SIZE} of each pack size</span>
            </div>
            <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
              <div className="flex justify-between text-xs font-bold text-ocean-900 dark:text-foam-50">
                <span>Chosen</span>
                <span data-testid="chosen">{kg} kg of ~{suggested} kg suggested</span>
              </div>
              <div role="progressbar" aria-label="Chosen weight against the suggestion" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-ocean-700/10 dark:bg-white/10">
                <div className="h-full rounded-full bg-teal-600 transition-all motion-reduce:transition-none" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <CatchPicker picks={picks} onChange={changePicks} idPrefix="party" />
          </section>

          <section className="flex flex-col gap-2 rounded-2xl bg-ocean-900 p-4 text-foam-50 shadow-md">
            <h2 className="font-display text-base font-bold">Something rare, or a very large order?</h2>
            <p className="text-sm text-foam-100/90">Tell us what you need and we&apos;ll see what we can do ({SUPPORT_HOURS}).</p>
            <div className="flex flex-wrap gap-2">
              <a href={`tel:${CONTACT_PHONE_HREF}`} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold text-ocean-900"><Phone className="h-4 w-4" aria-hidden="true" /> {CONTACT_PHONE_DISPLAY}</a>
              <a href={getWhatsAppLink("Hi ZenFresh, I'm planning a large order for a gathering.")} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-full bg-whatsapp px-4 text-sm font-bold text-ocean-950"><FaWhatsapp className="h-4 w-4" aria-hidden="true" /> WhatsApp<span className="sr-only"> (opens in a new tab)</span></a>
            </div>
          </section>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 px-4 pb-4 pt-3 shadow-[0_-6px_20px_rgba(0,0,0,0.08)] backdrop-blur-md dark:bg-ocean-950/95">
          <div className="mx-auto flex max-w-xl items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50" data-testid="crate-total">{formatInr(price)}</p>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{packs} {packs === 1 ? "pack" : "packs"} · {kg} kg · plus delivery at checkout</p>
            </div>
            {added ? (
              <Link to="/checkout" className="inline-flex min-h-12 shrink-0 items-center gap-2 rounded-full bg-teal-700 px-5 font-bold text-white shadow-md"><Check className="h-5 w-5" aria-hidden="true" /> Added — go to checkout</Link>
            ) : (
              <button type="button" onClick={addAll} disabled={packs === 0} className="inline-flex min-h-12 shrink-0 items-center gap-2 rounded-full bg-ocean-800 px-5 font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-50"><ShoppingCart className="h-5 w-5" aria-hidden="true" /> Add crate to cart</button>
            )}
          </div>
          <p role="status" className="sr-only">{added ? "Your crate was added to the cart." : ""}</p>
        </div>
      </div>
    </PageTransition>
  );
}
